/**
 * Maps Firebase Auth error codes to user-friendly messages.
 *
 * Returns `null` for codes that represent a deliberate user action
 * (e.g. closing the Google popup) — callers should show no error at all.
 */

const CANCELLED_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

const FRIENDLY_MESSAGES = {
  'auth/invalid-email': "That email address doesn't look right. Please check it and try again.",
  'auth/missing-email': 'Please enter your email address.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'Network problem. Please check your connection and try again.',
  'auth/popup-blocked': 'Your browser blocked the Google sign-in window. Allow popups for this site, or use the email link below instead.',
  'auth/account-exists-with-different-credential': 'This email is already registered with a different sign-in method. Try the email link instead.',
  'auth/user-disabled': 'This account has been disabled. Please contact us if you think this is a mistake.',
  'auth/operation-not-allowed': "Sign-in is temporarily unavailable. Please try again later or contact us if it persists.",
  'auth/quota-exceeded': 'Sign-in is temporarily unavailable. Please try again in a few minutes.',
  'auth/expired-action-code': 'This link has expired. Please request a new sign-in link.',
  'auth/invalid-action-code': 'This sign-in link is invalid or has already been used. Please request a new one.',
};

const DEFAULT_MESSAGE = "Something went wrong signing you in. Please try again — if it keeps happening, use the email link instead.";

/**
 * @param {string|undefined} code - Firebase error code, e.g. "auth/popup-blocked"
 * @returns {string|null} A friendly message, or null when no error should be shown.
 */
export function friendlyAuthError(code) {
  if (code && CANCELLED_CODES.has(code)) return null;
  return (code && FRIENDLY_MESSAGES[code]) || DEFAULT_MESSAGE;
}
